import Permissions, { usePermissions } from '@app/src/auth/permissions'
import CategoryCell from '@app/src/components/Table/Cells/CategoryCell'
import CheckboxCell from '@app/src/components/Table/Cells/CheckboxCell'
import CompanyCell from '@app/src/components/Table/Cells/CompanyCell'
import CountryCell from '@app/src/components/Table/Cells/CountryCell'
import DateCell from '@app/src/components/Table/Cells/DateCell'
import EnrichedValueCell from '@app/src/components/Table/Cells/EnrichedValueCell'
import FinalRiskRatingCell from '@app/src/components/Table/Cells/FinalRiskRatingCell'
import ProviderOverallRiskCell from '@app/src/components/Table/Cells/ProviderOverallRiskCell'
import ProviderConnectedCell from '@app/src/components/Table/Cells/ProviderConnectedCell'
import ProviderSpendCell from '@app/src/components/Table/Cells/ProviderSpendCell'
import ReferralContactCell from '@app/src/components/Table/Cells/ReferralContactCell'
import TagsCell from '@app/src/components/Table/Cells/TagsCell'
import UserGroupCell from '@app/src/components/Table/Cells/UserGroupCell'
import ValueCell from '@app/src/components/Table/Cells/ValueCell'
import { useProviderEnrichedValues } from '@app/src/hooks/useProviderEnrichedValues'
import type { Category, CategoryWithId } from '@app/src/types/categories'
import type { Provider } from '@app/src/types/organizations'
import { ProviderLinkStatus } from '@app/src/types/organizations'
import type { RiskStatus, Unit } from '@app/src/types/resourceExplorer'
import { TableRow } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

interface ProviderRowProps {
  row: Provider
  onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>, provider: Provider) => void
  selectedProviderIds: Array<number>
  categories?: Category[]
  organizationCurrency?: Unit
  spendPeriodFocus?: string
  overallRiskStatus?: RiskStatus
  isLoadingOverallRisk?: boolean
}

export const ProviderRow: React.FC<ProviderRowProps> = ({
  row: provider,
  onCheckboxChange,
  selectedProviderIds,
  categories,
  organizationCurrency,
  spendPeriodFocus,
  overallRiskStatus,
  isLoadingOverallRisk,
}) => {
  const { formatMessage } = useIntl()
  const { hasPermission } = usePermissions()
  const { topicalRisk = false } = useFlags()

  const isChecked = Boolean(selectedProviderIds?.find(providerId => providerId === provider.id))

  const selectedPeriodSpend = provider.spends?.find(spend => spend.periodName === spendPeriodFocus)
  const organizationContactUsers = provider.organization?.contacts?.map(contact => contact.user)

  const enrichedValues = useProviderEnrichedValues(provider)

  const categoryOptionsById = useMemo(() => {
    const grouped = new Map<number, string[]>()
    provider.categoryOptions?.forEach(opt => {
      if (opt.categoryId) {
        const existing = grouped.get(opt.categoryId) ?? []
        existing.push(opt.name)
        grouped.set(opt.categoryId, existing)
      }
    })
    return grouped
  }, [provider.categoryOptions])

  return (
    <TableRow selected={isChecked} data-testid="provider-row">
      <CheckboxCell checked={isChecked} onChange={e => onCheckboxChange(e, provider)} />
      <CompanyCell company={provider} drawer maxRows={1} />
      {topicalRisk && (
        <ProviderOverallRiskCell provider={provider} riskStatus={overallRiskStatus} isLoading={isLoadingOverallRisk} />
      )}
      <ProviderConnectedCell provider={provider} data-testid="provider-status" />
      <CountryCell countryCode={provider.country?.countryCode} />
      {provider.linkStatus === ProviderLinkStatus.Connected ? (
        <UserGroupCell
          users={organizationContactUsers}
          subTitle={provider.name}
          label={formatMessage({ id: 'general.contacts' }, { count: organizationContactUsers?.length })}
          title={formatMessage({ id: 'schemas.provider.contacts' })}
        />
      ) : (
        <ReferralContactCell provider={provider} drawer />
      )}
      <UserGroupCell
        users={provider.ownerUsers}
        subTitle={provider.name}
        label={formatMessage({ id: 'general.owners' }, { count: provider.ownerUsers?.length })}
        title={formatMessage({ id: 'schemas.provider.ownerUsers' })}
      />
      <EnrichedValueCell
        value={enrichedValues.websiteAddress.value}
        isEnriched={enrichedValues.websiteAddress.isEnriched}
        isLink
        data-testid="provider-website"
      />

      {spendPeriodFocus && organizationCurrency && (
        <ProviderSpendCell spend={selectedPeriodSpend} organizationCurrency={organizationCurrency} />
      )}

      <EnrichedValueCell value={enrichedValues.industry.value} isEnriched={enrichedValues.industry.isEnriched} />
      <EnrichedValueCell
        value={enrichedValues.registrationNumber.value}
        isEnriched={enrichedValues.registrationNumber.isEnriched}
      />
      <EnrichedValueCell value={enrichedValues.vatNumber.value} isEnriched={enrichedValues.vatNumber.isEnriched} />
      <EnrichedValueCell
        value={
          enrichedValues.revenueRange.value
            ? formatMessage({ id: `claim.companyRevenue.${enrichedValues.revenueRange.value}` })
            : undefined
        }
        isEnriched={enrichedValues.revenueRange.isEnriched}
      />
      <EnrichedValueCell
        value={
          enrichedValues.companySizeRange.value
            ? formatMessage({ id: `claim.companySize.${enrichedValues.companySizeRange.value}` })
            : undefined
        }
        isEnriched={enrichedValues.companySizeRange.isEnriched}
      />

      {!hasPermission(Permissions.FINANCE_USER) && (
        <CategoryCell
          value={formatMessage({ id: `schemas.provider.activityStatusValues.${provider.activityStatus}` })}
          defaultValue={formatMessage({ id: `schemas.provider.activityStatusValues.NotSelected` })}
        />
      )}

      <FinalRiskRatingCell value={provider.finalRiskRating} />
      <CategoryCell
        value={formatMessage({ id: `schemas.provider.priorityValues.${provider.priority}` })}
        defaultValue={formatMessage({ id: `schemas.provider.priorityValues.NotSelected` })}
      />

      {!hasPermission(Permissions.FINANCE_USER) && (
        <CategoryCell
          value={formatMessage({
            id: `schemas.provider.providerApprovalStatusValues.${provider.providerApprovalStatus}`,
          })}
          defaultValue={formatMessage({ id: `schemas.provider.providerApprovalStatusValues.NotSelected` })}
        />
      )}

      {hasPermission(Permissions.SOURCING_USER) && (
        <>
          <CategoryCell
            value={formatMessage(
              {
                id: `schemas.provider.tierValues`,
              },
              { tier: provider.tier ?? 0 },
            )}
            defaultValue={formatMessage(
              {
                id: `schemas.provider.tierValues`,
              },
              { tier: 0 },
            )}
          />
          <CategoryCell
            value={formatMessage({
              id: `schemas.provider.supplierUsageValues.${provider.supplierUsage ?? 'NotSelected'}`,
            })}
            defaultValue={formatMessage({ id: `schemas.provider.supplierUsageValues.NotSelected` })}
          />
        </>
      )}
      <ValueCell value={provider.customId} />
      {categories
        ?.filter((category): category is CategoryWithId => category.id !== undefined)
        .map(category => (
          <TagsCell key={category.id} amountOfTagsShow={2} tags={categoryOptionsById.get(category.id) ?? []} />
        ))}
      <DateCell value={provider.createdAt} />
    </TableRow>
  )
}

export default ProviderRow
